import React from 'react';
import { Stack, Typography, Button } from '@mui/material';
import { Error as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';

export interface ErrorStateProps {
  /** Main error message to display */
  message: string;
  /** Optional detailed description */
  description?: string;
  /** Optional retry callback - if provided, shows a retry button */
  onRetry?: () => void;
  /** Text for the retry button */
  retryText?: string;
  /** Whether retry is in progress */
  isRetrying?: boolean;
  /** Optional custom icon */
  icon?: React.ReactNode;
}

/**
 * A reusable error state component that displays an error message
 * with an optional retry button.
 *
 * @example
 * <ErrorState
 *   message="Failed to load tools"
 *   description="Please check your connection and try again."
 *   onRetry={() => loadTools()}
 * />
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  message,
  description,
  onRetry,
  retryText = 'Try Again',
  isRetrying = false,
  icon,
}) => {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        py: 6,
        px: 2,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-error-50, #FEE2E2)',
          color: 'var(--color-error-600, #DC2626)',
        }}
      >
        {icon || <ErrorIcon sx={{ fontSize: 28 }} />}
      </div>

      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {message}
        </Typography>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
            {description}
          </Typography>
        )}
      </Stack>

      {onRetry && (
        <Button
          variant="outlined"
          color="primary"
          onClick={onRetry}
          disabled={isRetrying}
          startIcon={<RefreshIcon />}
          sx={{ mt: 1 }}
        >
          {isRetrying ? 'Retrying...' : retryText}
        </Button>
      )}
    </Stack>
  );
};

export default ErrorState;
